import { TBctxItemWithoutSizeAndBctxKey, TInputAppStore, TSelectedBctxInfo } from '@monorepo/types';
import { InputAppClientDBActionTypes, TInputAppActions } from './input-app-client-db.action';
import { BCTXComputationHelper } from '@monorepo/utils';
import produce from 'immer';
import {
  INITIAL_CACHE_RESET_TIMESTAMP,
  WAITING_TIME_FOR_NEXT_EVENTS_SYNCING_IN_MILLI_SECONDS,
} from '../metadata/metadata.reducer';
export const DEFAULT_LINE_SHIFT_INCOMPLETE_BUNDLE_INFO = {
  lastUpdatedAt: INITIAL_CACHE_RESET_TIMESTAMP,
  list: [],
};
export const DEFAULT_BCTX_KEY = '--';
export const DEFAULT_SELECTED_BCTX: TBctxItemWithoutSizeAndBctxKey = {
  list: [],
  bctxKey: DEFAULT_BCTX_KEY,
};
export const DEFAULT_SELECTED_BCTX_INFO: TSelectedBctxInfo = {
  bctxInfoWithoutSize: DEFAULT_SELECTED_BCTX,
};
export function getInputAppInitialState(nextCacheResetTimestamp: number) {
  const inputAppInitialState: TInputAppStore = {
    userDBLockDetails: {
      isLocked: false,
      lockedBy: 'NONE',
    },
    toBeRectifiedListing: {},
    perPieceTransactions: {},
    perPieceQcCurrentActionInfoMap: {},
    userDB: {
      dailyCache: {
        lastStyleSyncInfoTimestamp: undefined,
        nextCacheResetTimestamp: nextCacheResetTimestamp,
        lineMap: {},
        ppLineMap: {},
        planBasedLineMap: {},
        events: {
          list: [],
        },
        bctxMinMaxDateTime: {
          minDate: null,
          maxDate: null,
        },
        bulkMdPointEvents: [],
      },
      persistanceCache: {
        incompleteBundlesInfo: {},
      },
    },
    bgDB: {
      dailyCache: {
        nextCacheResetTimestamp,
        lineMap: {},
        ppLineMap: {},
        events: {
          list: [],
        },
      },
      persistanceCache: {
        pastDaysList: [],
        nextEventId: 1,
        bulkQcPastDaysDefectives: {},
        pastDaysBackgroundEventsSyncInfo: {},
      },
    },
    bgDBLockDetails: {
      isLocked: false,
      lockedBy: 'NONE',
      metadata: {
        SYNC_EVENTS: {},
      },
    },
  };
  return inputAppInitialState;
}
export const inputAppInitialState: TInputAppStore = getInputAppInitialState(INITIAL_CACHE_RESET_TIMESTAMP);

export function InputAppClientDBStateReducer(
  state: TInputAppStore = inputAppInitialState,
  action: TInputAppActions,
): TInputAppStore {
  switch (action.type) {
    case InputAppClientDBActionTypes.UpdatePerPieceQcCurrentActionInfo: {
      return produce(state, (draft) => {
        draft.perPieceQcCurrentActionInfoMap[action.stationId] = action.currentActionInfo;
      });
    }
    case InputAppClientDBActionTypes.UpdatePerPieceLineInTransactions: {
      return produce(state, (draft) => {
        draft.perPieceTransactions = {
          response: action.response,
        };
      });
    }
    case InputAppClientDBActionTypes.ClearAndReFetchPerPieceLineInTransactions:
    case InputAppClientDBActionTypes.ClearPerPieceLineInTransactions: {
      return produce(state, (draft) => {
        draft.perPieceTransactions = {};
      });
    }
    case InputAppClientDBActionTypes.UpdatedWipAndActuals: {
      const bctx_key = action.selectedBctx.bctxInfoWithoutSize.bctxKey;
      const newBctxKey = action.selectedBctx.newBctxForPOSplit?.bctx.bctxKey;
      const { stationId, shiftId } = action.selectedStationIdAndShiftId;
      return produce(state, (draft) => {
        // BCTX Map update

        // Update wip map for old Bctx key in case of PO Split
        const sizeToUpdateInBctxMap =
          draft.userDB.dailyCache.lineMap[stationId].bctxWipMap[bctx_key].sizeToWipMap[action.sizeName];

        // Update shift map for new Bctx key in case of PO Split
        const shiftMapUpdate =
          draft.userDB.dailyCache.lineMap[stationId].shiftMap[shiftId].clientBctx[newBctxKey ? newBctxKey : bctx_key]
            .sizeMap[action.sizeName].client_work[action.actionType];

        if (action.undo) {
          sizeToUpdateInBctxMap.wip_qty = sizeToUpdateInBctxMap.wip_qty + action.count;
          shiftMapUpdate.actuals = shiftMapUpdate.actuals - action.count;
        } else {
          sizeToUpdateInBctxMap.wip_qty = sizeToUpdateInBctxMap.wip_qty - action.count;
          shiftMapUpdate.actuals = shiftMapUpdate.actuals + action.count;
        }
      });
    }
    case InputAppClientDBActionTypes.UpdateToBeRectifiedListingAndClientDBAndReleaseLockForSyncEventsForOnlineApps: {
      return produce(state, (draft) => {
        draft.toBeRectifiedListing = action.toBeRectifiedListing;
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.userDB = action.currentDB;
      });
    }
    case InputAppClientDBActionTypes.UpdateToBeRectifiedDataAndReleaseLockAndThenDismissLoader: {
      return produce(state, (draft) => {
        draft.toBeRectifiedListing.response = action.payload;
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.ClearToBeRectifiedListingAndReleaseLockAndThenDismissModal: {
      return produce(state, (draft) => {
        draft.toBeRectifiedListing = {};
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.InitializeReduxStoreSuccess: {
      return produce(state, (draft) => {
        draft.userDB = action.inputAppClientDB;
      });
    }
    case InputAppClientDBActionTypes.UpdateUserDBAndThenReleaseLockAndThenRestoreActualsForBackedUpBctx:
    case InputAppClientDBActionTypes.UpdateUserDBAndThenReleaseLockAndThenRestoreActualsForNewStyles:
    case InputAppClientDBActionTypes.SetUserDBAndReleaseLock:
    case InputAppClientDBActionTypes.SetUserDbAndAcquireUserDBLockAndThenRestoreActualsForBackedUpBctx:
    case InputAppClientDBActionTypes.SetUserDBAndReleaseLockAndThenLoadMasterdataLists:
    case InputAppClientDBActionTypes.SetUserDBAndReleaseLockAndThenEmitBctxChangeEvent:
    case InputAppClientDBActionTypes.SetUserDBAndReleaseLockAndThenFetchNewSizeList:
    case InputAppClientDBActionTypes.UpdateUserDBAndReleaseLockAndSyncStyleInfo:
    case InputAppClientDBActionTypes.UpdateIncompleteBundlesInfoAndReleaseLock:
    case InputAppClientDBActionTypes.SetClientDBAndReleaseLockAndThenRestoreToBeRectifiedCounts: {
      return produce(state, (draft) => {
        draft.userDB = action.inputAppClientDB;
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.SetUserDBAndClientDBAndReleaseLockAndEmitInitInfo:
    case InputAppClientDBActionTypes.SetUserDBAndBgDBAndReleaseLocksWithChains:
    case InputAppClientDBActionTypes.SetUserDBAndClientDBAndReleaseLock: {
      return produce(state, (draft) => {
        draft.userDB = action.inputAppClientDB;
        draft.bgDB = action.backgroundEventsSyncingDB;
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.UpdatedSelectedBctxAndReleaseLockAndThenDismissModal:
    case InputAppClientDBActionTypes.UpdateBctxWithoutChangeEventSuccessAndReleaseLock:
    case InputAppClientDBActionTypes.UpdateSelectedBctxItemAndReleaseLockAndThenAddEvent: {
      return produce(state, (draft) => {
        if (
          action.type === InputAppClientDBActionTypes.UpdatedSelectedBctxAndReleaseLockAndThenDismissModal &&
          action.isPPLineMapUsed
        ) {
          draft.userDB.dailyCache.ppLineMap[action.stationId].selectedBctxInfo = action.newBctx;
        } else {
          draft.userDB.dailyCache.lineMap[action.stationId].selectedBctxInfo = action.newBctx;
        }
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.UpdateUserDBAndReleaseLockForSyncEventsForOnlineApps: {
      return produce(state, (draft) => {
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.userDB = action.currentDB;
      });
    }
    case InputAppClientDBActionTypes.UpdateUserDBAndThenReleaseUserDBLockAndThenSyncEvents: {
      return produce(state, (draft) => {
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.userDB = action.updatedDB;
      });
    }
    case InputAppClientDBActionTypes.UpdateUserDBAndThenReleaseUserDBLockAndThenHandleSideActions: {
      return produce(state, (draft) => {
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.userDB = action.updatedDB;
      });
    }
    case InputAppClientDBActionTypes.ReleaseUserDBLock:
    case InputAppClientDBActionTypes.ReleaseUserDBLockAndShowPopupAndDismissLoader: {
      return produce(state, (draft) => {
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.UpdateLockDetails: {
      return produce(state, (draft) => {
        draft.userDBLockDetails = action.lockDetails;
      });
    }
    case InputAppClientDBActionTypes.UpdateBgDB: {
      return produce(state, (draft) => {
        draft.bgDB = action.currentDB;
      });
    }
    case InputAppClientDBActionTypes.UpdateBgDBAndReleaseLockForSyncEvents: {
      return produce(state, (draft) => {
        const currentTime = new Date().getTime();
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(
          draft.bgDBLockDetails,
          action.syncEventsDTO.lastBatchSyncStatus === 'SERVER_ERROR'
            ? WAITING_TIME_FOR_NEXT_EVENTS_SYNCING_IN_MILLI_SECONDS + currentTime
            : undefined,
        );
        draft.bgDB = action.currentDB;
      });
    }
    case InputAppClientDBActionTypes.ReleaseBgDBLock: {
      return produce(state, (draft) => {
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.updateNextEventIDandThenReleaseBgDBLock: {
      return produce(state, (draft) => {
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
        draft.bgDB.persistanceCache.nextEventId = action.nextEventID;
      });
    }
    case InputAppClientDBActionTypes.updatebgDBEventsListandNextEventIdAndReleaseUserAndBgDBLock: {
      return produce(state, (draft) => {
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.bgDB.dailyCache.events.list = action.bgDBEventsList;
        draft.bgDB.persistanceCache.nextEventId = action.nextEventID;
      });
    }
    case InputAppClientDBActionTypes.updateNextEventIDandThenReleaseUserAndBgDBLock: {
      return produce(state, (draft) => {
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.bgDB.persistanceCache.nextEventId = action.nextEventID;
      });
    }
    case InputAppClientDBActionTypes.UpdateUserDBAndBgDBEventListWithNextEventIdAndThenReleaseLocks: {
      return produce(state, (draft) => {
        draft.userDB = action.updatedDB;
        draft.bgDB.dailyCache.events.list = action.bgDBEventsList;
        draft.bgDB.persistanceCache.nextEventId = action.nextEventID;
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.UpdateBgDBLockDetails: {
      return produce(state, (draft) => {
        draft.bgDBLockDetails = action.lockDetails;
      });
    }
    case InputAppClientDBActionTypes.ReleaseBgAndUserDbLockAndShowFailedEventsAction: {
      return produce(state, (draft) => {
        draft.userDB = action.userDb;
        draft.bgDB = action.bgDb;
        draft.bgDBLockDetails = BCTXComputationHelper.releaseBgLock(draft.bgDBLockDetails);
        draft.userDBLockDetails = BCTXComputationHelper.releaseUserDBLock(draft.userDBLockDetails);
      });
    }
    case InputAppClientDBActionTypes.InsertNewBctxSizeListIntoDBSuccessAndReleaseLock: {
      return produce(state, (draft) => {
        draft.userDB = action.currentDB;
      });
    }
    default:
      return state;
  }
}
